import { HtmlMarkerInstanceProps, HtmlMarkerSetProps } from '@abyss/3d-viewer';
import { useAtomValue } from 'jotai';
import {
  hoveredCustomBlock as hoveredCustomBlockAtom,
  hoveredCustomBlockDefects as hoveredCustomBlockDefectsAtom,
} from '../state';
import {
  CustomBlockHighlightMarker,
  CustomBlockHighlightMarkerProps,
} from '../components/CustomBlockHighlightMarker';
import { useMemo } from 'react';
import { CustomBlockDefects } from '@/types';
import { UnitSystemEnum } from '@/__generated__/graphql';
import { unitSystem as unitSystemAtom } from '@/components/Analysis/state';
import { roundNumber, SQFEET_TO_SQMETERS_FACTOR } from '@/utils/unitSystem';

const getRoundedDefectValue = ({
  defectsArray,
  type,
  unitSystem,
}: {
  defectsArray: CustomBlockDefects[0]['totalArea'] | undefined;
  type: string;
  unitSystem: UnitSystemEnum;
}): number | undefined => {
  const found = defectsArray?.find((item) => item?.type === type);
  if (!found?.value) return;
  if (typeof found?.value !== 'number') return;
  const converted =
    unitSystem === UnitSystemEnum.Imperial ? found.value * SQFEET_TO_SQMETERS_FACTOR : found.value;
  return roundNumber(converted, 2);
};

export const useCustomBlockHighlightMarkers = () => {
  const hoveredCustomBlock = useAtomValue(hoveredCustomBlockAtom);
  const hoveredCustomBlockDefects = useAtomValue(hoveredCustomBlockDefectsAtom);
  const unitSystem = useAtomValue(unitSystemAtom);

  const instances: HtmlMarkerInstanceProps[] = useMemo(() => {
    if (!hoveredCustomBlock || !hoveredCustomBlockDefects) return [];

    const totalSurfaceArea = getRoundedDefectValue({
      defectsArray: hoveredCustomBlockDefects.totalArea,
      type: 'area',
      unitSystem,
    });

    const ri4plusri5 = getRoundedDefectValue({
      defectsArray: hoveredCustomBlockDefects.totalArea,
      type: 'Ri4+Ri5',
      unitSystem,
    });

    const props: CustomBlockHighlightMarkerProps = {
      name: hoveredCustomBlock.name,
      totalSurfaceArea,
      ri4plusri5,
      unit: unitSystem,
    };

    return [
      {
        id: hoveredCustomBlock.id,
        position: hoveredCustomBlock.position,
        children: <CustomBlockHighlightMarker {...props} />,
        distanceThreshold: 1000,
        scale: 7,
      },
    ];
  }, [hoveredCustomBlock, hoveredCustomBlockDefects, unitSystem]);

  const markerSet: HtmlMarkerSetProps = useMemo(
    () => ({
      id: 'custom-blocks-highlight-markers',
      instances,
    }),
    [instances]
  );

  return { markerSet };
};
